import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import showToast from '@/utils/toast';
import { styled } from '@mui/material';
import { CloudUpload, Delete } from '@mui/icons-material';
import { uploadWithPresignedUrl } from '@/utils/file';
import { InputRules } from '@/types/inputs';
import { buildInputRulesObject } from '@/utils/buildInputRulesObject';
import { useGetBrandingThemePresignedUrl } from '@/hooks/requests/useBrandingThemes';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

type FileUploadProps = {
  label: string;
  name: string;
  rules?: InputRules;
  fileName: string;
  acceptedFileTypes?: string;
};

// TODO: Needs to be deprecated once new SVFilePicker supports branding themes
export default function BrandingThemeFilePicker({
  label,
  name,
  rules,
  fileName,
  acceptedFileTypes = '.png,.jpg,.jpeg',
}: FileUploadProps) {
  const { getBrandingThemePresignedUrl } = useGetBrandingThemePresignedUrl();

  const methods = useFormContext();
  const [isUploading, setIsUploading] = useState(false);
  const builtRulesObject = buildInputRulesObject(label, rules);

  const file = methods.watch(name);

  const removeFile = () => {
    methods.setValue(name, '');
  };

  if (file) {
    return (
      <Box sx={{ flexDirection: 'row', display: 'flex', alignItems: 'center' }}>
        <Typography sx={{ color: 'primary' }} fontWeight={'bold'}>
          {file.name ? file.name : file.split('/').pop()}
        </Typography>
        <IconButton onClick={removeFile} aria-label={`remove ${label} file`}>
          <Delete />
        </IconButton>
      </Box>
    );
  }

  const handleUploadFileToS3 = async (file: File) => {
    setIsUploading(true);

    try {
      const preSignedUrlResponse = await getBrandingThemePresignedUrl({
        file: fileName,
        file_extension: file.type.replace(/(.*)\//g, ''),
      });

      const uploadImageFileResponse = await uploadWithPresignedUrl(
        preSignedUrlResponse,
        file,
      );

      methods.setValue(name, uploadImageFileResponse);
      setIsUploading(false);
    } catch {
      setIsUploading(false);
      showToast({
        type: 'error',
        data: { content: `Upload file ${file.name} failed` },
      });
    }
  };

  return (
    <Controller
      name={name}
      control={methods.control}
      rules={builtRulesObject}
      render={({ field: { value, onBlur, ref }, fieldState: { error } }) => {
        if (isUploading) {
          return <CircularProgress />;
        }

        return (
          <>
            <Button
              component="label"
              role={undefined}
              variant="contained"
              tabIndex={-1}
              startIcon={<CloudUpload />}>
              {label}
              <VisuallyHiddenInput
                data-testid="fileUploadInput"
                value={value?.fileName}
                onChange={event => {
                  if (event.target.files) {
                    handleUploadFileToS3(event.target.files[0]);
                  }
                }}
                onBlur={onBlur}
                ref={ref}
                type="file"
                id="picture"
                accept={acceptedFileTypes}
              />
            </Button>
            {error && (
              <Typography sx={{ color: 'error' }}>{error?.message}</Typography>
            )}
          </>
        );
      }}
    />
  );
}
